import { useCallback } from 'react';
import { useHistory } from '@compat/router';
import { v1 } from 'uuid';
import { cartToken } from '@store/CartStore';
import { useStores } from '@root/src/stores';

interface UseAuthLoginParams {
  isBarMenu?: boolean;
  onLogin?: () => void;
}

interface LoginParams {
  cartCookieId?: string | null;
  cookieId: string;
  settingsItem: any;
}

export const useAuthLogin = ({
  isBarMenu,
  onLogin
}: UseAuthLoginParams): ((params: LoginParams) => void) => {
  const { userStore, modalsStore, cartStore } = useStores();
  const history = useHistory();

  return useCallback(
    ({ cartCookieId, cookieId, settingsItem }: LoginParams): void => {
      const { changeAuthToMerge } = modalsStore;
      const { setSettings, getFullUserInfo } = userStore;
      const { initCartStore } = cartStore;
      const targetCartToken = cartCookieId || v1();
      const sourceCartToken = cartStore?.cart?.cookieId || cartToken;

      const finishLogin = (): void => {
        if (onLogin) {
          onLogin();
          return;
        }
        if (!isBarMenu) {
          history.go(0);
        }
      };

      const pathname = history.location.pathname;
      const useMenuCartLookup = !!isBarMenu && pathname.includes('/bar-menu/');

      setSettings(settingsItem);
      getFullUserInfo(cookieId, targetCartToken)
        .then(async () => {
          await initCartStore({
            pathname,
            isUserLoggedIn: true,
            token: useMenuCartLookup ? '' : targetCartToken,
            resolveUserMenuCart: useMenuCartLookup,
            menuPeopleId: userStore.user?.id
          });

          const targetCart = cartStore?.cart;
          const loadedTargetToken = targetCart?.cookieId || targetCartToken;
          const isMenuCart = !!targetCart?.is_menu;
          const isTargetValid = isBarMenu ? isMenuCart : !isMenuCart;

          if (
            !isTargetValid ||
            !sourceCartToken ||
            sourceCartToken === loadedTargetToken
          ) {
            finishLogin();
            return;
          }

          changeAuthToMerge(
            {
              targetCartToken: loadedTargetToken,
              sourceCartToken,
              registration: false
            },
            finishLogin
          );
        })
        .catch(error => {
          console.log(error);
          finishLogin();
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartStore, isBarMenu, onLogin]
  );
};

export default useAuthLogin;
